'use client'

import * as React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSettingsStore } from '@/lib/store'
import { useI18n } from '@/lib/i18n'

// Resizable, collapsible shell for the map view's side panel (user request
// 2026-08-16: "close / open / change width of the left panel").
//
// - Width lives in the settings store (persisted) — a user who drags it to
//   560px wants it there next time too.
// - Drag handle on the right edge; pointer events (not mouse) so it also
//   works with a pen/touch, and `setPointerCapture` keeps the drag alive when
//   the cursor outruns the 6px handle. Width is written to state on every
//   move — the panel is a plain flex child so that's a cheap layout, and it
//   keeps the map's flex-1 in lockstep without a resize observer.
// - Collapsed = the panel unmounts entirely; the way back is MapListHandle
//   below, which the map renders so it outlives this component.
// - Clamped [MIN, MAX]: below MIN the cards can't lay out; above MAX the map
//   is a sliver.
const MIN_W = 300
const MAX_W = 720

export function MapSidePanel({ children }: { children: React.ReactNode }) {
  const { t } = useI18n()
  const width = useSettingsStore((s) => s.mapPanelWidth)
  const open = useSettingsStore((s) => s.mapPanelOpen)
  const setWidth = useSettingsStore((s) => s.setMapPanelWidth)
  const setOpen = useSettingsStore((s) => s.setMapPanelOpen)
  const [dragging, setDragging] = React.useState(false)
  const startRef = React.useRef<{ x: number; w: number } | null>(null)

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    ;(e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId)
    startRef.current = { x: e.clientX, w: width }
    setDragging(true)
  }
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const s = startRef.current
    if (!s) return
    const next = Math.round(Math.min(MAX_W, Math.max(MIN_W, s.w + (e.clientX - s.x))))
    if (next !== width) setWidth(next)
  }
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!startRef.current) return
    startRef.current = null
    setDragging(false)
    try {
      ;(e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId)
    } catch {
      /* already released */
    }
  }

  // Collapsed the panel is simply gone; MapListHandle (rendered over the map)
  // is the single control for both states — design web/search.jsx ListHandle.
  if (!open) return null

  return (
    <div
      className={cn(
        'relative flex flex-shrink-0 min-h-0',
        // No width transition while dragging — it would lag the pointer.
        !dragging && 'transition-[width] duration-150'
      )}
      style={{ width }}
    >
      <div className="min-w-0 flex-1 overflow-y-auto border-r border-border bg-background">
        {children}
      </div>

      {/* Drag handle: a 6px hit strip on the panel's right edge with a 2px
          visible grip that darkens on hover/drag. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t('search.panelResize', 'Resize list')}
        title={t('search.panelResize', 'Resize list')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={() => setWidth(440)}
        className={cn(
          'absolute -right-[3px] top-0 z-30 h-full w-[6px] cursor-col-resize group',
          dragging && 'select-none'
        )}
      >
        <div
          className={cn(
            'mx-auto h-full w-[2px] transition-colors',
            dragging ? 'bg-primary' : 'bg-transparent group-hover:bg-outline'
          )}
        />
      </div>
    </div>
  )
}

// Seam handle — design web/search.jsx `ListHandle`, ported verbatim.
//
// One control for BOTH states rather than two buttons: open, it rides the
// divider between list and map (left:-14, straddling the seam) as a bare
// chevron; collapsed, it slides onto the map's left edge (left:12) and grows
// a label naming what comes back — "1 137 / BYTŮ" — so it never reads as a
// dead arrow. `[` toggles it, per the design's shortcut.
//
// Rendered by the MAP, not the panel, so it survives the panel unmounting.
//
// Deviation from the design source: hover is CSS (group-hover) rather than
// React state. The design tracked hover in state only because inline styles
// can't express `:hover` — same visual, no re-render.
export function MapListHandle({ count }: { count: number }) {
  const { t } = useI18n()
  const open = useSettingsStore((s) => s.mapPanelOpen)
  const setOpen = useSettingsStore((s) => s.setMapPanelOpen)

  // Czech needs three plural forms: 1 byt · 2–4 byty · 5+ bytů.
  const homes =
    count === 1
      ? t('search.home', 'home')
      : count >= 2 && count <= 4
        ? t('search.homeFew', 'homes')
        : t('search.homes', 'homes')

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '[' || e.metaKey || e.ctrlKey || e.altKey) return
      const el = e.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return
      setOpen(!open)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, setOpen])

  const label = open
    ? t('search.panelClose', 'Hide the list — press [')
    : `${t('search.panelOpen', 'Show the list')} · ${count.toLocaleString('cs-CZ')} ${homes} — [`

  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      title={label}
      aria-label={label}
      aria-expanded={open}
      className={cn(
        // Deviation from the design's z-20: open, this handle straddles the
        // seam and so lands exactly on the panel's resize grip (z-30), which
        // the design has no equivalent of. At z-20 the grip swallowed every
        // click on the handle's centre — the toggle was dead where you would
        // actually aim. z-40 puts the toggle on top; the grip keeps the rest
        // of its ~1000px for resizing.
        'group absolute top-1/2 z-40 flex h-16 -translate-y-1/2 items-center rounded-full',
        'border border-outline-variant bg-surface/[0.92] shadow-e1 backdrop-blur-[8px] hover:shadow-e2'
      )}
      style={{
        left: open ? -14 : 12,
        gap: open ? 0 : 6,
        padding: open ? '0 4px' : '0 12px 0 8px',
        // Inline, not an arbitrary class: a multi-property transition with
        // commas collapses to `transition: all` through Tailwind, and the
        // design's `var(--ease-spatial)` has no CSS-var counterpart here —
        // it is the `spatial` easing in tailwind.config.
        transition:
          'left 0.24s cubic-bezier(0.3, 1.3, 0.4, 1), padding 0.24s cubic-bezier(0.3, 1.3, 0.4, 1), box-shadow 0.18s',
      }}
    >
      {open ? (
        <ChevronLeft size={20} className="text-ink-secondary group-hover:text-primary" />
      ) : (
        <ChevronRight size={20} className="text-ink-secondary group-hover:text-primary" />
      )}
      {!open && (
        <span className="flex flex-col items-start leading-[1.25]">
          <span className="font-mono text-[13px] font-bold">{count.toLocaleString('cs-CZ')}</span>
          <span className="text-[10px] font-bold uppercase tracking-[0.9px] text-ink-muted">{homes}</span>
        </span>
      )}
    </button>
  )
}
